import { useCallback, useEffect, useRef, useState } from 'react'
import { monitoringAPI } from '../services/monitoringApi'
import type { MonitoringDashboard, TimeRange } from '../types/monitoring'

const AUTO_REFRESH_SECONDS = 30

export function useServerMetrics(serverId: string = 'default', initialRange: TimeRange = '1h') {
  const [range, setRange] = useState<TimeRange>(initialRange)
  const [data, setData] = useState<MonitoringDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECONDS)

  // Guards against a slow response from an old range landing after the
  // user has already switched to a new range.
  const requestToken = useRef(0)

  const fetchData = useCallback(async (isManual = false, rangeOverride?: TimeRange) => {
    const token = ++requestToken.current
    const effectiveRange = rangeOverride ?? range
    setLoading((prev) => prev || isManual)
    setError('')
    try {
      const res = await monitoringAPI.getDashboard(serverId, effectiveRange)
      if (token !== requestToken.current) return
      setData(res.data)
      setLastRefresh(new Date())
      setCountdown(AUTO_REFRESH_SECONDS)
    } catch (err: any) {
      if (token !== requestToken.current) return
      setError(err.response?.data?.detail || 'Failed to load server metrics. Retrying shortly…')
    } finally {
      if (token === requestToken.current) setLoading(false)
    }
  }, [serverId, range])

  useEffect(() => { fetchData(true) }, [fetchData])

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchData()
          return AUTO_REFRESH_SECONDS
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [fetchData])

  const changeRange = useCallback((next: TimeRange) => {
    setRange(next)
    fetchData(true, next)
  }, [fetchData])

  return {
    data, loading, error, range, lastRefresh, countdown,
    autoRefreshSeconds: AUTO_REFRESH_SECONDS,
    refresh: () => fetchData(true),
    changeRange,
  }
}
